import type {IExtractedProperty, IPropertiesMap, IPropertyData, PropertySpecialState} from "@buildwithflux/models";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";

/**
 * Hook to get all document properties
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useDocumentProperties = (): IPropertiesMap | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.properties.all));
};

/**
 * Hook to get a property by UID
 * @param uid Property UID to retrieve
 */
export const usePropertyByUid = (uid: string): IPropertiesMap[string] | undefined => {
    return useFluxServices().useDocumentStore(select.properties.byUid(uid));
};

/**
 * Hook to get multiple properties by UIDs
 * @param uids Property UIDs to retrieve
 */
export const usePropertiesByUids = (uids: string[]): Array<IPropertiesMap[string]> => {
    return useFluxServices().useDocumentStore(useShallow(select.properties.byUids(uids)));
};

/**
 * Hook to get all property UIDs
 */
export const usePropertyUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.properties.uids));
};

/**
 * Hook to get property count
 */
export const usePropertyCount = (): number => {
    return useFluxServices().useDocumentStore(select.properties.count);
};

/**
 * Hook to check if document has any properties
 */
export const useHasProperties = (): boolean => {
    return useFluxServices().useDocumentStore(select.properties.hasProperties);
};

/**
 * Hook to get all property names - memoized
 */
export const usePropertyNames = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.properties.names));
};

/**
 * Hook to get the datasheet property.
 * Uses shallow comparison to avoid unnecessary re-renders when the property object reference changes.
 */
export const useDatasheetProperty = (): IPropertyData | IExtractedProperty | PropertySpecialState | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.properties.datasheet));
};

/**
 * Hook to get the manufacturer part number property.
 * Uses shallow comparison to avoid unnecessary re-renders when the property object reference changes.
 */
export const useManufacturerPartNumberProperty = ():
    | IPropertyData
    | IExtractedProperty
    | PropertySpecialState
    | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.properties.manufacturerPartNumber));
};
